<template>
    <div>
        <!-- both overlays above the mobile detail card (z-index 8 on its v-main) -->
        <v-overlay :value="qrDialogOverlay" z-index="9" :style="`width:${bgWidth}px;`" :dark="false" :color="overlayColor" :opacity="0.95">
            <QrDialogPage
                v-if="qrDialogOverlay"
                @cancel="onQrCancel"
                @success="onQrSuccess"
                :class="qrAnimationClass"
            />
        </v-overlay>

        <v-overlay :value="passwordDialogOverlay" z-index="9" :style="`width:${bgWidth}px;`" :dark="false" :color="overlayColor" :opacity="0.95">
            <PasswordDialogPage
                v-if="passwordDialogOverlay"
                @cancel="onPasswordCancel"
                @success="onPasswordSuccess"
                :class="passwordAnimationClass"
            />
        </v-overlay>
    </div>
</template>

<script>
import CONFIG from "../../constants/config";
import {layoutMixin} from "../../utils/layout-mixin";
import {spotMixin} from "../../utils/spot-mixin";
import {EventBus} from "./EventBus";
import QrDialogPage from "../../views/dashboard/QrDialogPage";
import PasswordDialogPage from "../../views/dashboard/PasswordDialogPage";

/**
 * QR scanner and password input for spot checkins, opened through the event bus from the
 * power button (free) or from a list tap on a spot without description (bound to that spot).
 * A found spot gets its checkin marked as verified and is emitted as checkin(spot, kind):
 * 'open' for the spot shown right now, 'secret' for a secret spot, 'list' for any other.
 * A code or password that matches no allowed spot shows the fail animation.
 */
export default {
    mixins: [layoutMixin, spotMixin],
    components: {
        QrDialogPage,
        PasswordDialogPage,
    },
    data() {
        return {
            qrDialogOverlay: false,
            passwordDialogOverlay: false,
            boundSpot: null,
            overlayColor: '#333',
            qrAnimationClass: '',
            passwordAnimationClass: '',
            animationFailedDuration: 1000,
            animationSuccessDuration: 1500,
        };
    },
    computed: {
        anyOpen() {
            return this.qrDialogOverlay || this.passwordDialogOverlay;
        },
        openSpotId() {
            return this.$store.getters['taskpage/openSpotId'];
        },
    },
    watch: {
        anyOpen(val) {
            this.$store.commit('taskpage/SetCheckinActionOpen', val);
        },
    },
    methods: {
        // request: { type: 'qr' | 'password', spot?: the spot the request is bound to }
        open(request) {
            this.boundSpot = request && request.spot ? request.spot : null;
            if (request && request.type === 'password') {
                this.passwordDialogOverlay = true;
            } else {
                this.qrDialogOverlay = true;
            }
        },
        /**
         * Bound: only the bound spot counts. Free: the spot shown right now, or a playable spot
         * (not inactive, not played) with the checkin type of the dialog.
         */
        allowedSpot(spot, hasCheckinType) {
            if (!spot) {
                return null;
            }
            if (this.boundSpot) {
                return spot.id === this.boundSpot.id ? spot : null;
            }
            if (spot.id === this.openSpotId) {
                return spot;
            }
            return !this.spotIsInactive(spot) && !this.spotAlreadyTouched(spot) && hasCheckinType(spot) ? spot : null;
        },
        finish(spot) {
            this.markCheckinVerified(spot.id);
            const kind = spot.id === this.openSpotId ? 'open' : this.isSecretSpot(spot) ? 'secret' : 'list';
            this.boundSpot = null;
            this.$emit('checkin', spot, kind);
        },

        onQrCancel() {
            this.qrDialogOverlay = false;
        },
        onQrSuccess(qrCode) {
            const spot = this.allowedSpot(this.getSpotByQrCode(qrCode), this.isCheckinTypeQr);
            if (spot) {
                this.showQrSuccessAnimation().then(() => {
                    this.qrDialogOverlay = false;
                    this.finish(spot);
                });
            } else {
                this.showQrFailAnimation().then(() => {
                    this.qrDialogOverlay = false;
                });
            }
        },
        onPasswordCancel() {
            this.passwordDialogOverlay = false;
        },
        // the dialog itself rejects unknown passwords; a known one may still belong to a spot that does not count
        onPasswordSuccess(foundSpot) {
            const spot = this.allowedSpot(foundSpot, this.isCheckinTypePasswort);
            if (spot) {
                this.showPasswordSuccessAnimation().then(() => {
                    this.passwordDialogOverlay = false;
                    this.finish(spot);
                });
            } else {
                this.showPasswordFailAnimation().then(() => {
                    this.passwordDialogOverlay = false;
                });
            }
        },

        showQrSuccessAnimation() {
            return this.animate('qrAnimationClass', 'bounce', CONFIG.getGreen(), this.animationSuccessDuration);
        },
        showQrFailAnimation() {
            return this.animate('qrAnimationClass', 'shake-horizontal', CONFIG.getRed(), this.animationFailedDuration);
        },
        showPasswordSuccessAnimation() {
            return this.animate('passwordAnimationClass', 'bounce', CONFIG.getGreen(), this.animationSuccessDuration);
        },
        showPasswordFailAnimation() {
            return this.animate('passwordAnimationClass', 'shake-horizontal', CONFIG.getRed(), this.animationFailedDuration);
        },
        animate(classField, animationClass, color, duration) {
            return new Promise((resolve) => {
                this[classField] = animationClass;
                this.overlayColor = color;
                setTimeout(() => {
                    this[classField] = '';
                    this.overlayColor = '#333';
                    resolve();
                }, duration);
            });
        },
    },
    mounted() {
        EventBus.$on(CONFIG.EVENT_BUS_OPEN_CHECKIN_ACTION, this.open);
    },
    beforeDestroy() {
        EventBus.$off(CONFIG.EVENT_BUS_OPEN_CHECKIN_ACTION, this.open);
    },
};
</script>

<style lang="scss" scoped>
.bounce {
    animation-duration: 2s;
    animation-iteration-count: infinite;
    animation-name: bounce;
    animation-timing-function: ease;
}

@keyframes bounce {
    0%   { transform: translateY(0); }
    30%  { transform: translateY(-100px); }
    50%  { transform: translateY(0); }
    100% { transform: translateY(0); }
}

.shake-horizontal {
    -webkit-animation: shake-horizontal 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
    animation: shake-horizontal 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
}

@-webkit-keyframes shake-horizontal {
    0%, 100% {
        -webkit-transform: translateX(0);
        transform: translateX(0);
    }
    10%, 30%, 50%, 70% {
        -webkit-transform: translateX(-10px);
        transform: translateX(-10px);
    }
    20%, 40%, 60% {
        -webkit-transform: translateX(10px);
        transform: translateX(10px);
    }
    80% {
        -webkit-transform: translateX(8px);
        transform: translateX(8px);
    }
    90% {
        -webkit-transform: translateX(-8px);
        transform: translateX(-8px);
    }
}

@keyframes shake-horizontal {
    0%, 100% {
        -webkit-transform: translateX(0);
        transform: translateX(0);
    }
    10%, 30%, 50%, 70% {
        -webkit-transform: translateX(-10px);
        transform: translateX(-10px);
    }
    20%, 40%, 60% {
        -webkit-transform: translateX(10px);
        transform: translateX(10px);
    }
    80% {
        -webkit-transform: translateX(8px);
        transform: translateX(8px);
    }
    90% {
        -webkit-transform: translateX(-8px);
        transform: translateX(-8px);
    }
}
</style>
